/* @jsx h */
import { api } from "@app/data/api";
import {
  PrintDoc,
  Printer,
  PrinterStatuses,
  PrintJob,
  Promotion,
  User,
  Service,
  Order,
  TableStatus,
  FoodType,
  FloorPlan,
} from "@lemmonhq/types";
import {
  createShopConnection,
  ShopConnection,
} from "@app/utils/shopConnection";
import {
  defineComponent,
  inject,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  Ref,
  ref,
  watch,
} from "vue";
import { useAuth } from "./Auth";
import { useToast } from "./Toast";
import { nanoid } from "nanoid";
import { useRoute } from "vue-router";
import { useTrpc } from "./TrpcProvider";
import { serverRouting } from "@app/data/helpers";

// Define ShopContext type
const ShopContextKey = Symbol("ShopContext");
export interface ShopContextType {
  booted: Ref<boolean>;
  getPrinters: () => Printer[] | undefined;
  getPromotions: () => Promotion[] | undefined;
  setPrinters: (printers: Printer[]) => void;
  print: (job: PrintJob) => Promise<void>;
  printReceipt: (doc: PrintDoc) => Promise<void>;
  printerStatuses: PrinterStatuses;
  services: Ref<Service[]>;
  bc: BroadcastChannel;
  // New additions
  orders: Ref<Order[]>;
  tables: Ref<TableStatus[]>;
  foodTypes: Ref<FoodType[]>;
  floorPlans: Ref<FloorPlan[]>;
  pendingReservationRequestsCount: Ref<number>;
  pendingReservationRequestIds: Ref<string[]>;
  refreshData: () => Promise<void>;
  refreshReservationRequests: () => Promise<void>;
  refreshTables: () => Promise<void>;
  refreshOrders: () => Promise<void>;
}

function initializeSessionId(): string {
  const LOCALSTORAGE_KEY = "LEMMON_SESSION_ID";
  let storedSessionId = localStorage.getItem(LOCALSTORAGE_KEY);
  if (!storedSessionId) {
    storedSessionId = nanoid();
    localStorage.setItem(LOCALSTORAGE_KEY, storedSessionId);
  }
  return storedSessionId;
}
const firebaseConfig =
  import.meta.env.VITE_ENV === "production"
    ? {
        apiKey: "AIzaSyB3Ms28qMCyJi_uisIj1g3b_BHM2HnK_5M",
        authDomain: "lemmon-production.firebaseapp.com",
        databaseURL:
          "https://lemmon-production-default-rtdb.europe-west1.firebasedatabase.app",
        projectId: "lemmon-production",
        storageBucket: "lemmon-production.firebasestorage.app",
        messagingSenderId: "509376130195",
        appId: "1:509376130195:web:69c399b1d3fb830fbe3166",
        measurementId: "G-WYP4Z9CXX3",
      }
    : {
        apiKey: "AIzaSyDMCh0Jen9QueuBTCPrpbHxvr0__fRtvLg",
        authDomain: "lemmon-staging.firebaseapp.com",
        projectId: "lemmon-staging",
        storageBucket: "lemmon-staging.firebasestorage.app",
        messagingSenderId: "986123602940",
        appId: "1:986123602940:web:36e34cfa5a80e5d84a0079",
        measurementId: "G-ZRWYM36DSF",
      };
export const ShopConnectionProvider = defineComponent({
  name: "ShopConnectionProvider",
  setup(_, { slots }) {
    // State variables
    const booted = ref<boolean>();
    const printers = ref<Printer[] | undefined | Error>(undefined);
    const services = ref<Service[] | undefined | Error>();
    const promotions = ref<Promotion[] | undefined | Error>(undefined);
    const orders = ref<Order[]>([]);
    const tables = ref<TableStatus[]>([]);
    const foodTypes = ref<FoodType[]>([]);
    const floorPlans = ref<FloorPlan[]>([]);
    const pendingReservationRequestsCount = ref<number>(0);
    const pendingReservationRequestIds = ref<string[]>([]);
    const shopConnection = ref<ShopConnection | null>(null);
    // Initialize session ID from localStorage or generate a new one
    const sessionId = ref<string>(initializeSessionId());
    const printerStatuses = reactive<PrinterStatuses>({
      timestamps: {},
    });

    // Dependencies
    const toast = useToast();
    const auth = useAuth();
    const trpc = useTrpc();

    // Fetch printers from the API
    const fetchPrinters = async () => {
      const list = await api.getPrinters();
      printers.value = list.map((el) => el.device);
    };
    // Fetch printers from the API
    const fetchPromotions = async () => {
      const list = await api.getPromotions();
      promotions.value = list;
    };
    const fetchServices = async () => {
      const list = await api.getServices();
      services.value = list;
    };

    const SHOP_UPDATES_INTERVAL = 30 * 1000;

    let shopUpdatesPollingInterval: ReturnType<typeof setInterval> | undefined;
    let isShopDataPolling = false;
    const lastShopEventId = ref<number>(0);

    const applyShopSnapshot = (snapshot: {
      lastEventId: number;
      orders: Order[];
      tables: TableStatus[];
      foodTypes: FoodType[];
      floorPlans: FloorPlan[];
      reservationRequestsCount: number;
      reservationRequestIds: string[];
    }) => {
      lastShopEventId.value = snapshot.lastEventId;
      orders.value = snapshot.orders;
      tables.value = snapshot.tables;
      foodTypes.value = snapshot.foodTypes;
      floorPlans.value = snapshot.floorPlans;
      pendingReservationRequestsCount.value = snapshot.reservationRequestsCount;
      pendingReservationRequestIds.value = snapshot.reservationRequestIds;
    };

    const fetchInitialData = async () => {
      try {
        const snapshot = await trpc.shop.getSnapshot.query();
        applyShopSnapshot(snapshot);
      } catch (error) {
        console.error("Failed to fetch shop snapshot:", error);
      }
    };

    const pollShopUpdates = async () => {
      if (isShopDataPolling) {
        return;
      }

      isShopDataPolling = true;
      try {
        const update = await trpc.shop.getUpdates.query({
          lastEventId: lastShopEventId.value,
        });

        lastShopEventId.value = update.lastEventId;
        // Piggybacked API routing value; skips the next /api/routing poll.
        // Keep this when replacing getUpdates (see shop.getUpdates).
        serverRouting.apply(update.preferredApiServer);

        if (!update.hasUpdates) {
          return;
        }

        if ("orders" in update && update.orders !== undefined) {
          orders.value = update.orders;
        }
        if ("tables" in update && update.tables !== undefined) {
          tables.value = update.tables;
        }
        if ("reservationRequestsCount" in update) {
          pendingReservationRequestsCount.value =
            update.reservationRequestsCount ??
            pendingReservationRequestsCount.value;
          pendingReservationRequestIds.value =
            update.reservationRequestIds ?? pendingReservationRequestIds.value;
        }
      } catch (error) {
        console.error("Failed to poll shop updates:", error);
      } finally {
        isShopDataPolling = false;
      }
    };

    const startDataPolling = () => {
      stopDataPolling();
      void fetchInitialData();
      shopUpdatesPollingInterval = setInterval(
        pollShopUpdates,
        SHOP_UPDATES_INTERVAL,
      );
    };

    const stopDataPolling = () => {
      if (shopUpdatesPollingInterval) {
        clearInterval(shopUpdatesPollingInterval);
        shopUpdatesPollingInterval = undefined;
      }
    };

    // Poll for updates immediately and reset the interval so the next
    // scheduled tick is SHOP_UPDATES_INTERVAL from now.
    const refreshData = async () => {
      await pollShopUpdates();
      stopDataPolling();
      shopUpdatesPollingInterval = setInterval(
        pollShopUpdates,
        SHOP_UPDATES_INTERVAL,
      );
    };

    const bc = new BroadcastChannel("shop");

    // Set up shop connection when auth user and printers are available
    const setupShopConnection = async () => {
      console.log("setupShopConnection", auth.user.value, printers.value);
      if (auth.user.value && Array.isArray(printers.value)) {
        const connection = createShopConnection(
          sessionId.value,
          auth.user.value,
          printers.value,
          toast,
          (printerId, isOnline) => {
            //console.log("printer", { printerId, isOnline });
            printerStatuses.timestamps[printerId.toString()] = isOnline
              ? Date.now()
              : 0;
          },
        );
        shopConnection.value = connection;
      }
    };

    const pingPrinters = () => {
      const connection = shopConnection.value;
      if (connection) {
        connection.pingPrinters();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        pingPrinters();
        void pollShopUpdates();
      }
    };
    const onWindowFocus = () => {
      pingPrinters();
      void pollShopUpdates();
    };
    const route = useRoute();
    // lets watch route to ping printers
    watch(() => route.fullPath, pingPrinters);
    onMounted(() => {
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("focus", onWindowFocus);
    });
    onUnmounted(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onWindowFocus);
    });

    const setup = async (user: Ref<User | null>) => {
      if (user.value) {
        if (user.value.role === "admin") {
          stopDataPolling();
          return;
        }
        if (shopConnection.value) {
          shopConnection.value.unsubscribe();
        }
        stopDataPolling();
        lastShopEventId.value = 0;
        await Promise.all([
          fetchPrinters(),
          fetchPromotions(),
          fetchServices(),
        ]);
        booted.value = true;
        setupShopConnection();
        startDataPolling(); // Add this line
      }
    };

    let userId = auth.user.value?.id;
    // Watch for changes in auth.user and printers
    watch([auth.user], async ([user]) => {
      console.log("watch triggered", user);
      if (auth.user.value?.id !== userId) {
        userId = auth.user.value?.id;
        setup(auth.user);
      }
    });

    const handleLocalBCMessage = (e: MessageEvent) => {
      if (e.data?.type === "logout") {
        window.location.href = "/";
      }
    };

    // Fetch printers on mount
    onMounted(async () => {
      setup(auth.user);
      bc.addEventListener("message", handleLocalBCMessage);
    });
    onUnmounted(() => {
      bc.removeEventListener("message", handleLocalBCMessage);
      stopDataPolling(); // Add this line
      if (shopConnection.value) {
        shopConnection.value.unsubscribe();
        shopConnection.value = null;
      }
    });

    // Provide ShopContext
    const context = {
      sessionId: sessionId.value,
      printerStatuses,
      services,
      // Existing methods
      getPromotions: () => promotions.value,
      getPrinters() {
        return printers.value;
      },
      setPrinters: (newPrinters: Printer[]) => {
        printers.value = newPrinters;
      },
      printReceipt: async (doc: PrintDoc) => {
        if (shopConnection.value) {
          return shopConnection.value.printReceipt(doc);
        } else {
          throw new Error("No shop connection available.");
        }
      },
      print: async (job: PrintJob) => {
        if (shopConnection.value) {
          return shopConnection.value.print(job);
        } else {
          throw new Error("No shop connection available.");
        }
      },
      bc,
      // New additions
      orders,
      tables,
      foodTypes,
      floorPlans,
      pendingReservationRequestsCount,
      pendingReservationRequestIds,
      refreshData,
      refreshReservationRequests: refreshData,
      refreshTables: refreshData,
      refreshOrders: refreshData,
      booted,
    };
    provide(ShopContextKey, context);

    return () => {
      return slots.default?.();
    };
  },
});

export const useShop = () => {
  const context = inject<ShopContextType>(ShopContextKey);
  if (!context) {
    throw new Error("useShop must be used within a ShopConnectionProvider");
  }
  return context;
};

//export const useFirebase = (): FirebaseApp => {
//  const context = inject<ShopContextType>(ShopContextKey);
//  if (!context) {
//    throw new Error("useShop must be used within a ShopConnectionProvider");
//  }
//  return context.firebase;
//};
